import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { getDomainFilterParams } from '../hooks/useDomainQuery';
import { sendOtpEmail } from './emailService';
import { applyDomainFilterToQuery } from '../hooks/useDomainQuery';

// Utiliser la même clé de stockage pour les OTP que celle utilisée dans customerProfileService
// pour éviter les conflits et les duplications
const OTP_STORAGE_PREFIX = 'customer_otp_';

export interface ElearningStudentProfile {
  id?: string;
  tenant_id?: string;
  email: string;
  full_name?: string;
  phone?: string;
  bio?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  birth_date?: string;
  avatar_url?: string;
  preferences?: Record<string, any>;
  access_token?: string;
  token_expires_at?: string;
  created_at?: string;
  updated_at?: string;
  // Champs pour les statistiques, pour la compatibilité avec ProfileData
  totalCourses?: number;
  completedCourses?: number;
  averageProgress?: number;
  totalBadges?: number;
  studyHours?: number;
  activeCourses?: number;
}

/**
 * Crée ou met à jour un profil étudiant
 */
export const createOrUpdateStudentProfile = async (profileData: ElearningStudentProfile): Promise<ElearningStudentProfile | null> => {
  try {
    // Vérifier si le profil existe déjà
    const { data: existingProfile } = await supabase
      .from('elearn_customer_profiles')
      .select('*')
      .eq('email', profileData.email)
      .single();

    if (existingProfile) {
      // Mettre à jour le profil existant
      const { data, error } = await supabase
        .from('elearn_customer_profiles')
        .update({
          full_name: profileData.full_name || existingProfile.full_name,
          phone: profileData.phone || existingProfile.phone,
          address: profileData.address || existingProfile.address,
          city: profileData.city || existingProfile.city,
          country: profileData.country || existingProfile.country,
          postal_code: profileData.postal_code || existingProfile.postal_code,
          preferences: { ...existingProfile.preferences, ...profileData.preferences },
          avatar_url: profileData.avatar_url || existingProfile.avatar_url,
          birth_date: profileData.birth_date || existingProfile.birth_date,
        })
        .eq('email', profileData.email)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      // Créer un nouveau profil
      const { data, error } = await supabase
        .from('elearn_customer_profiles')
        .insert([
          {
            ...profileData,
            avatar_url: profileData.avatar_url,
            birth_date: profileData.birth_date,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  } catch (error) {
    console.error('Erreur lors de la création/mise à jour du profil étudiant:', error);
    return null;
  }
};

/**
 * Récupère un profil étudiant par email
 */
export const getStudentProfileByEmail = async (email: string): Promise<ElearningStudentProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('elearn_customer_profiles')
      .select('*')
      .eq('email', email)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération du profil étudiant:', error);
    return null;
  }
};

/**
 * Récupère un profil étudiant par token d'accès SANS filtrage par domaine
 * Le token est unique et suffit pour identifier l'utilisateur (SSO multi-domaine)
 */
export const getStudentProfileByToken = async (token: string, domain?: string, tenantId?: string): Promise<ElearningStudentProfile | null> => {
  try {
    // Ne pas filtrer par domaine - le token est unique et valide sur tous les domaines
    const { data, error } = await supabase
      .from('elearn_customer_profiles')
      .select('*')
      .eq('access_token', token)
      .gt('token_expires_at', new Date().toISOString())
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération du profil étudiant par token:', error);
    return null;
  }
};

/**
 * Récupère un profil étudiant par email avec filtrage par domaine
 */
export const getStudentProfileByEmailAndDomain = async (email: string, domain?: string, tenantId?: string): Promise<ElearningStudentProfile | null> => {
  try {
    let query = supabase
      .from('elearn_customer_profiles')
      .select('*')
      .eq('email', email);

    // Appliquer le filtrage par domaine avec fallback sur tenant_id
    if (domain && tenantId) {
      query = query.or(`domain.eq.${domain},and(domain.is.null,tenant_id.eq.${tenantId})`);
    } else if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }

    const { data, error } = await query.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération du profil étudiant par email et domaine:', error);
    return null;
  }
};

/**
 * Génère un code OTP à 6 chiffres
 */
const generateOTP = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

/**
 * Envoie un code OTP par email
 */
export const sendStudentOTP = async (email: string): Promise<string | null> => {
  try {
    const otp = generateOTP();
    
    // Stocker l'OTP dans localStorage (en production, utiliser une méthode plus sécurisée)
    localStorage.setItem(`${OTP_STORAGE_PREFIX}${email}`, otp);
    localStorage.setItem(`${OTP_STORAGE_PREFIX}expiry_${email}`, (Date.now() + 10 * 60 * 1000).toString()); // 10 minutes d'expiration
    
    // Envoyer l'email avec l'OTP
    await sendOtpEmail({
      to: email,
      name: email,
      otpCode: otp
    });
    
    return otp;
  } catch (error) {
    console.error('Erreur lors de l\'envoi du code OTP:', error);
    return null;
  }
};

/**
 * Vérifie un code OTP
 */
export const verifyStudentOTP = (email: string, otp: string): boolean => {
  const storedOTP = localStorage.getItem(`${OTP_STORAGE_PREFIX}${email}`);
  const expiryTime = localStorage.getItem(`${OTP_STORAGE_PREFIX}expiry_${email}`);
  
  // Vérifier si l'OTP est expiré
  if (!storedOTP || !expiryTime || parseInt(expiryTime) < Date.now()) {
    return false;
  }
  
  // Vérifier si l'OTP est correct
  return storedOTP === otp;
};

/**
 * Génère un token d'accès pour un étudiant
 */
export const generateStudentAccessToken = async (email: string): Promise<string | null> => {
  try {
    const token = uuidv4();
    
    // Définir une date d'expiration (30 jours)
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 30);
    
    // Vérifier si le profil existe
    const { data: profile } = await supabase
      .from('elearn_customer_profiles')
      .select('*')
      .eq('email', email)
      .single();
    
    if (profile) {
      // Mettre à jour le token dans elearn_customer_profiles
      const { error: elearnError } = await supabase
        .from('elearn_customer_profiles')
        .update({
          access_token: token,
          token_expires_at: expiresAt.toISOString()
        })
        .eq('email', email);
      
      if (elearnError) throw elearnError;
      
      // Synchroniser manuellement le token vers psy et shop
      const { domain, tenantId } = getDomainFilterParams();
      
      await supabase
        .from('psy_patients')
        .update({
          access_token: token,
          token_expires_at: expiresAt.toISOString(),
          last_login_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('email', email)
        .eq('tenant_id', tenantId);
      
      await supabase
        .from('shop_customer_profiles')
        .update({
          access_token: token,
          token_expires_at: expiresAt.toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('email', email);
    } else {
      // Créer un nouveau profil
      const { error } = await supabase
        .from('elearn_customer_profiles')
        .insert([{
          email,
          access_token: token,
          token_expires_at: expiresAt.toISOString()
        }]);
      
      if (error) throw error;
    }
    
    return token;
  } catch (error) {
    console.error('Erreur lors de la génération du token d\'accès:', error);
    return null;
  }
};

/**
 * Invalide un token d'accès (déconnexion)
 */
export const invalidateStudentAccessToken = async (token: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('elearn_customer_profiles')
      .update({
        access_token: null,
        token_expires_at: null
      })
      .eq('access_token', token);

    if (error) throw error;
    
    // Supprimer le token du localStorage
    localStorage.removeItem('elearning_student_token');
    // SSO: supprimer également le token shop
    localStorage.removeItem('customer_access_token');
    
    return true;
  } catch (error) {
    console.error('Erreur lors de l\'invalidation du token d\'accès:', error);
    return false;
  }
};

/**
 * Récupère les inscriptions d'un étudiant par son profil
 */
export const getStudentEnrollments = async (profileId: string, tenantId: string): Promise<any[]> => {
  try {
    let query = supabase
      .from('elearn_enrollments')
      .select(`
        *,
        course:course_id(id, title, slug, thumbnail_url, description, duration_hours, difficulty_level, instructor:instructor_id(name, avatar_url))
      `)
      .eq('student_id', profileId)
      .order('enrolled_at', { ascending: false });
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Erreur lors de la récupération des inscriptions de l\'étudiant:', error);
    return [];
  }
};

/**
 * Vérifie si un étudiant a accès à un cours spécifique
 */
export const isStudentEnrolledInCourse = async (profileId: string, tenantId: string, courseId: string): Promise<boolean> => {
  try {
    let query = supabase
      .from('elearn_enrollments')
      .select('id')
      .eq('student_id', profileId)
      .eq('course_id', courseId)
      .eq('status', 'active');
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query.single();

    if (error) return false;
    return !!data;
  } catch (error) {
    console.error('Erreur lors de la vérification de l\'accès au cours:', error);
    return false;
  }
};

/**
 * Récupère les détails d'une inscription spécifique pour un étudiant
 */
export const getStudentEnrollmentDetails = async (profileId: string, tenantId: string, enrollmentId: string): Promise<any | null> => {
  try {
    console.log('getStudentEnrollmentDetails - Début de la fonction', { profileId, tenantId, enrollmentId });
    // D'abord, récupérer l'inscription pour obtenir le course_id
    let enrollmentQuery = supabase
      .from('elearn_enrollments')
      .select('*') // Récupérer uniquement les champs de base sans jointure
      .eq('student_id', profileId)
      .eq('id', enrollmentId)
      .eq('status', 'active');
    
    enrollmentQuery = applyDomainFilterToQuery(enrollmentQuery);
    const { data: enrollment, error: enrollmentError } = await enrollmentQuery.single();
      
    if (enrollmentError) {
      console.log('getStudentEnrollmentDetails - Erreur lors de la récupération de l\'inscription:', enrollmentError);
      throw enrollmentError;
    }
    if (!enrollment) {
      console.log('getStudentEnrollmentDetails - Aucune inscription trouvée');
      return null;
    }
    console.log('getStudentEnrollmentDetails - Inscription trouvée:', enrollment);
    
    // Ensuite, récupérer les détails du cours
    if (enrollment.course_id) {
      console.log('getStudentEnrollmentDetails - Récupération des détails du cours avec ID:', enrollment.course_id);
      
      // 1. Récupérer les informations de base du cours
      let courseQuery = supabase
        .from('elearn_courses')
        .select(`
          *,
          instructor:instructor_id(id, name, bio, avatar_url)
        `)
        .eq('id', enrollment.course_id);
      
      courseQuery = applyDomainFilterToQuery(courseQuery);
      const { data: courseData, error: courseError } = await courseQuery.single();
        
      if (courseError) {
        console.log('getStudentEnrollmentDetails - Erreur lors de la récupération du cours:', courseError);
        throw courseError;
      }
      if (!courseData) {
        console.log('getStudentEnrollmentDetails - Aucune donnée de cours trouvée');
        return enrollment;
      }
      console.log('getStudentEnrollmentDetails - Données du cours récupérées:', courseData);
      
      // 2. Récupérer les modules du cours
      console.log('getStudentEnrollmentDetails - Récupération des modules du cours');
      let modulesQuery = supabase
        .from('elearn_modules')
        .select('*')
        .eq('course_id', enrollment.course_id)
        .order('sort_order', { ascending: true });
      
      modulesQuery = applyDomainFilterToQuery(modulesQuery);
      const { data: modulesData, error: modulesError } = await modulesQuery;
        
      if (modulesError) {
        console.log('getStudentEnrollmentDetails - Erreur lors de la récupération des modules:', modulesError);
        throw modulesError;
      }
      
      console.log('getStudentEnrollmentDetails - Modules récupérés:', modulesData?.length || 0, 'modules');
      
      // 3. Récupérer les leçons pour chaque module
      const modulesWithLessons = [];
      
      if (modulesData && modulesData.length > 0) {
        console.log('getStudentEnrollmentDetails - Récupération des leçons pour chaque module');
        
        for (const module of modulesData) {
          const { data: lessonsData, error: lessonsError } = await supabase
            .from('elearn_lessons')
            .select('*')
            .eq('module_id', module.id)
            .order('sort_order', { ascending: true });
            
          if (lessonsError) {
            console.log(`getStudentEnrollmentDetails - Erreur lors de la récupération des leçons pour le module ${module.id}:`, lessonsError);
            throw lessonsError;
          }
          
          modulesWithLessons.push({
            ...module,
            lessons: lessonsData || []
          });
          
          console.log(`getStudentEnrollmentDetails - Leçons récupérées pour le module ${module.id}:`, lessonsData?.length || 0, 'leçons');
        }
      }
      
      // Combiner les données
      const combinedData = {
        ...enrollment,
        course: {
          ...courseData,
          modules: modulesWithLessons
        },
        modules: modulesWithLessons
      };
      
      // Vérification de la structure des données avant de les retourner
      console.log('getStudentEnrollmentDetails - Structure des données:', {
        'enrollment_id': combinedData.id,
        'course_id': combinedData.course_id,
        'modules_count': combinedData.modules?.length || 0,
        'first_module': combinedData.modules?.length > 0 ? {
          'id': combinedData.modules[0].id,
          'title': combinedData.modules[0].title,
          'lessons_count': combinedData.modules[0].lessons?.length || 0
        } : 'Aucun module'
      });
      
      // Vérifier si les modules et leçons sont correctement définis
      if (!combinedData.modules || combinedData.modules.length === 0) {
        console.warn('getStudentEnrollmentDetails - ATTENTION: Aucun module trouvé dans les données combinées');
      } else if (combinedData.modules.some((module: { lessons?: any[] }) => !module.lessons || module.lessons.length === 0)) {
        console.warn('getStudentEnrollmentDetails - ATTENTION: Certains modules n\'ont pas de leçons');
      }
      
      console.log('getStudentEnrollmentDetails - Données combinées prêtes à être retournées');
      return combinedData;
    }
    
    console.log('getStudentEnrollmentDetails - Retour des données d\'inscription sans modules:', enrollment);
    return enrollment;
  } catch (error) {
    console.error('Erreur lors de la récupération des détails de l\'inscription:', error);
    return null;
  } finally {
    console.log('getStudentEnrollmentDetails - Fin de la fonction');
  }
};
